import { useSession } from "@clerk/clerk-react";
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  CalendarBlank,
  NotePencil,
} from "@phosphor-icons/react";
import { Spinner, Tooltip } from "@heroui/react";
import { API_URL } from "constants/index";
import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import {
  ContactCallLog,
  ContactCallDirection,
  CONTACT_CALL_TYPE_LABELS,
  CONTACT_CALL_DIRECTION_LABELS,
} from "shared/types/contact-call-log";

interface CallLogsSectionProps {
  contactId: number;
  refreshTrigger?: unknown;
}

/**
 * Section displaying call logs for a contact
 */
export const CallLogsSection = ({
  contactId,
  refreshTrigger,
}: CallLogsSectionProps) => {
  const { session } = useSession();
  const [callLogs, setCallLogs] = useState<ContactCallLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch call logs
  const fetchCallLogs = useCallback(async () => {
    if (!contactId) return;

    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/contact/call-logs/contact/${contactId}`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setCallLogs(data);
      }
    } catch (error) {
      console.error("Failed to fetch call logs:", error);
    } finally {
      setIsLoading(false);
    }
  }, [contactId, session]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs, refreshTrigger]);

  // Format duration
  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get icon for call direction
  const getDirectionIcon = (direction: ContactCallDirection) => {
    return direction === ContactCallDirection.INBOUND ? (
      <PhoneIncoming size={14} className="text-moss-primary" />
    ) : (
      <PhoneOutgoing size={14} className="text-blue-500" />
    );
  };

  if (isLoading) {
    return (
      <div className="border-neutral-primary/15 flex flex-col gap-2 rounded-md border bg-white p-4">
        <div className="text-neutral-primary flex items-center gap-2 text-sm font-medium">
          <Phone weight="duotone" size={16} />
          Gesprekken
        </div>
        <div className="flex items-center justify-center py-4">
          <Spinner size="sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="border-neutral-primary/15 flex flex-col gap-2 rounded-md border bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-neutral-primary flex items-center gap-2 text-sm font-medium">
          <Phone weight="duotone" size={16} />
          Gesprekken ({callLogs.length})
        </div>
      </div>

      {callLogs.length === 0 ? (
        <p className="text-neutral-secondary-mid py-2 text-sm">
          Nog geen gesprekken gelogd
        </p>
      ) : (
        <div className="mt-2 flex flex-col gap-2">
          {callLogs.map((log) => (
            <div
              key={log.id}
              className="flex flex-col gap-1 rounded-lg border border-neutral-100 bg-neutral-50 p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getDirectionIcon(log.callDirection)}
                  <span className="text-sm font-medium">
                    {CONTACT_CALL_DIRECTION_LABELS[log.callDirection]}
                  </span>
                  <span className="text-neutral-secondary-mid text-xs">
                    {CONTACT_CALL_TYPE_LABELS[log.callType]}
                  </span>
                </div>
                <div className="text-neutral-secondary-mid flex items-center gap-3 text-xs">
                  {log.durationSeconds && (
                    <Tooltip content="Duur">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatDuration(log.durationSeconds)}
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip content="Datum">
                    <span className="flex items-center gap-1">
                      <CalendarBlank size={12} />
                      {formatDate(log.callDate)}
                    </span>
                  </Tooltip>
                </div>
              </div>

              {log.phoneNumber && (
                <p className="text-neutral-secondary-mid text-xs">
                  {log.phoneNumber}
                </p>
              )}

              {log.notes && (
                <div className="text-neutral-primary mt-1 flex items-start gap-1 rounded bg-white p-2 text-sm">
                  <NotePencil
                    size={14}
                    className="mt-0.5 flex-shrink-0 text-neutral-400"
                  />
                  <p className="whitespace-pre-wrap">{log.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CallLogsSection;
